import { hasQueuedTurnStart } from "@t3tools/client-runtime/state/thread-settled";
import type {
  OrchestrationThreadShell,
  ProjectVmProfileConfig,
  ProjectVmProfileProposal,
} from "@t3tools/contracts";

export const VM_SNAPSHOT_BLOCKED_COPY = "Wait for the agent to finish before creating a VM Build.";
export const VM_SNAPSHOT_RESTARTED_COPY =
  "The VM service restarted before this Build finished. Your saved environment and secrets are unchanged. Prepare a new Build to try again.";

const LEGACY_VM_SNAPSHOT_RESTARTED_COPY =
  "The VM service restarted before the snapshot produced a Build artifact.";

export function formatVmBuildError(error: string): string {
  return error === LEGACY_VM_SNAPSHOT_RESTARTED_COPY ? VM_SNAPSHOT_RESTARTED_COPY : error;
}

export type VmProfileProposalEligibility = {
  readonly canApply: boolean;
  readonly canSave: boolean;
  readonly reason: "blocked" | "workspace-changes" | "project-changes" | null;
};

export function preserveVmProfileSecretNames(
  config: ProjectVmProfileConfig,
  existingNames: ReadonlyArray<string>,
): ProjectVmProfileConfig {
  return {
    ...config,
    secretNames: Array.from(new Set([...config.secretNames, ...existingNames])),
  };
}

/**
 * A host-side setup proposal is reproducible only when its worktree is clean.
 * A Build-preparation VM is different: every task bootstrap resets project
 * source to the requested commit, so files generated by the running service do
 * not become part of the next task's checkout.
 */
export function getVmProfileProposalEligibility(input: {
  readonly proposal: ProjectVmProfileProposal | null;
  readonly workspaceChanges: ReadonlyArray<string>;
  readonly taskSourceResetsAtBootstrap?: boolean;
}): VmProfileProposalEligibility {
  const { proposal, taskSourceResetsAtBootstrap = false, workspaceChanges } = input;
  if (proposal === null) {
    return { canApply: false, canSave: true, reason: null };
  }
  if (proposal.status === "blocked") {
    return { canApply: false, canSave: false, reason: "blocked" };
  }
  if (workspaceChanges.length > 0 && !taskSourceResetsAtBootstrap) {
    return { canApply: false, canSave: false, reason: "workspace-changes" };
  }
  if (proposal.requirements.projectChanges.length > 0) {
    return { canApply: false, canSave: false, reason: "project-changes" };
  }
  return { canApply: true, canSave: true, reason: null };
}

export type VmSnapshotBlockReason = "queued" | "running" | null;

export type VmSettingsRowIdentityState = {
  readonly ids: ReadonlyArray<string>;
  readonly nextId: number;
};

export function vmProfileProposalRefreshKey(input: {
  readonly threadId: string | undefined;
  readonly completedAt: string | null;
  readonly running: boolean;
  readonly profileUpdatedAt: string | null;
}): string | null {
  if (input.threadId === undefined || input.completedAt === null || input.running) return null;
  const completedAtMs = Date.parse(input.completedAt);
  const profileUpdatedAtMs =
    input.profileUpdatedAt === null ? Number.NaN : Date.parse(input.profileUpdatedAt);
  if (
    Number.isFinite(completedAtMs) &&
    Number.isFinite(profileUpdatedAtMs) &&
    profileUpdatedAtMs >= completedAtMs
  ) {
    return null;
  }
  return `${input.threadId}:${input.completedAt}`;
}

export function createVmSettingsRowIdentityState(
  prefix: "port" | "verification",
  count: number,
): VmSettingsRowIdentityState {
  return {
    ids: Array.from({ length: count }, (_, index) => `${prefix}-${index}`),
    nextId: count,
  };
}

export function appendVmSettingsRowIdentity(
  state: VmSettingsRowIdentityState,
  prefix: "port" | "verification",
): VmSettingsRowIdentityState {
  return {
    ids: [...state.ids, `${prefix}-${state.nextId}`],
    nextId: state.nextId + 1,
  };
}

export function removeVmSettingsRowIdentity(
  state: VmSettingsRowIdentityState,
  index: number,
): VmSettingsRowIdentityState {
  return {
    ids: state.ids.filter((_, candidateIndex) => candidateIndex !== index),
    nextId: state.nextId,
  };
}

export function selectVmStatusCandidates<T extends Pick<OrchestrationThreadShell, "updatedAt">>(
  threads: ReadonlyArray<T>,
  limit: number,
): ReadonlyArray<T> {
  return threads
    .toSorted((left, right) => right.updatedAt.localeCompare(left.updatedAt))
    .slice(0, limit);
}

export function getVmSnapshotBlockReason(
  thread: Pick<OrchestrationThreadShell, "latestTurn" | "latestUserMessageAt" | "session">,
  options: { readonly now: string },
): VmSnapshotBlockReason {
  if (thread.latestTurn?.state === "running") return "running";
  if (thread.session?.status === "starting" || thread.session?.status === "running") {
    return "running";
  }
  if (hasQueuedTurnStart(thread, options)) return "queued";
  return null;
}
